// react
import { MouseEvent, MouseEventHandler, useState, useContext } from 'react';

// i18n
import { useTranslation } from 'react-i18next';

// router
import { Link as RouterLink, useSearchParams } from 'react-router-dom';

// hooks
import { useAuth } from '../../../hooks/useAuth';
import { useFetch } from '../../../hooks/useFetch';

// mui
import {
	AppBar,
	Avatar,
	Box,
	Button,
	Chip,
	Grid,
	IconButton,
	InputAdornment,
	Link,
	ListItemText,
	MenuItem,
	MenuList,
	Popover,
	TextField,
	Toolbar,
	Tooltip,
	Typography,
} from '@mui/material';

// types
import { Activity } from '../../../@types';

// context
import { ContextUserEntity } from '../../../App.context';

// ui
import { LineItemProps } from '../LineItem/LineItem';
import { Notifications } from './Notifications/Notifications';

// links
import { LinkLearning } from '../../links/LinkLearning/LinkLearning';

// fields
import { SelectUserEntity } from '../../fields/SelectUserEntity/SelectUserEntity';

// icons
import {
	HelpOutlineOutlined as IconHelpOutline,
	MenuOutlined as IconMenu,
	NotificationsOutlined as IconNotifications,
	SearchOutlined as IconSearch,
} from '@mui/icons-material';

// img
import { LogoFundsChainAlt } from '../../img/LogoFundsChainAlt';

// utils
import { constructActivityItems, paramPopulateActivities } from '../../../utils/activities';
import { translateRole } from '../../../utils/translations';

//dialog
import { DialogAddIncident } from '../../dialogs/DialogAddIncident/DialogAddIncident';

// theme
import { theme } from '../../../theme';

// config
import { featureFlags } from '../../../config/featureFlags';

// props
export interface HeaderProps {
	onOpenNav?: MouseEventHandler;
}

export type Anchor = HTMLButtonElement | null;

export const Header = ({ onOpenNav }: HeaderProps) => {
	// hooks
	const { logout } = useAuth();
	const { t } = useTranslation();

	// context
	const { userEntity } = useContext(ContextUserEntity);

	// session
	const accessToken = sessionStorage.getItem('accessToken');
	const idToken = sessionStorage.getItem('idToken');

	// state
	const [activities, setActivities] = useState<Activity[]>([]);

	// vars
	const sessionUser = sessionStorage.getItem('user');
	const user = sessionUser ? JSON.parse(sessionUser) : null;

	let urlFetchActivities = `${process.env.REACT_APP_API_URL}/activities?type=!PAYMENT_CREATED&populate=${paramPopulateActivities}&limit=10&orderBy=created`;

	if (user?.abac?.projects?.length) {
		urlFetchActivities += `&project=${user.abac.projects.join(',')}`;
	}

	// fetch activities
	useFetch({
		isEnabled: Boolean(user),
		url: urlFetchActivities,
		options: {
			headers: {
				Authorization: `Bearer ${accessToken}`,
				User: String(idToken),
			},
		},
		onSuccess: (res) => {
			if (res.data) {
				setActivities(res.data);
			}
		},
	});

	// items
	const activityItems: LineItemProps[] = constructActivityItems({
		activities,
		t,
	});

	// state
	const [anchorElAccount, setAnchorElAccount] = useState<Anchor>(null);
	const [anchorElNotifications, setAnchorElNotifications] = useState<Anchor>(null);
	const [searchParams, setSearchParams] = useSearchParams();

	let userChipLabel = null;

	if (user) {
		userChipLabel = translateRole(user.role, t);
	}

	const handleOpenAccount = (e: MouseEvent<HTMLButtonElement>) => {
		setAnchorElAccount(e.currentTarget);
	};

	const handleOpenNotifications = (e: MouseEvent<HTMLButtonElement>) => {
		setAnchorElNotifications(e.currentTarget);
	};

	const handleCloseAccount = () => {
		setAnchorElAccount(null);
	};

	const handleCloseNotifications = () => {
		setAnchorElNotifications(null);
	};

	const handleLogout = (e: MouseEvent<HTMLButtonElement>) => {
		setAnchorElAccount(null);
		logout();
	};

	const paramDialog = searchParams.get('dialog');
	const handleOpenFeedback = () => {
		setSearchParams((params) => {
			params.set('dialog', 'addIncident');
			return params;
		});
	};

	const handleCloseFeedback = () => {
		setSearchParams((params) => {
			params.delete('dialog');
			return params;
		});
	};

	let backgroundColor = theme.palette.brand.blue[800];
	let envName = '';

	if (process.env.REACT_APP_ENV) {
		if (['development', 'local', 'qa'].includes(process.env.REACT_APP_ENV)) {
			backgroundColor = theme.palette.brand.blue[700];
		}

		if (process.env.REACT_APP_ENV === 'development') {
			envName = t('env.dev');
		} else if (process.env.REACT_APP_ENV === 'qa') {
			envName = t('env.qa');
		} else if (process.env.REACT_APP_ENV === 'local') {
			envName = t('env.local');
		}
	}

	return (
		<AppBar
			elevation={0}
			position="static"
			sx={{
				backgroundColor,
				alignItems: 'center',
				borderRadius: '0 !important',
				flexDirection: 'row',
				height: theme.variables?.headerHeight,
			}}>
			<Toolbar
				disableGutters={true}
				sx={{
					height: '100%',
					justifyContent: 'space-between',
					minHeight: '0 !important',
					padding: theme.spacing(0, 2),
					width: '100%',
				}}>
				<Grid
					alignItems="center"
					columnSpacing={2}
					container
					sx={{ justifyContent: { xs: 'space-between', lg: 'flex-end' } }}>
					<Grid item xs={4} md={3} lg={5} sx={{ display: { lg: 'none' } }}>
						<Box
							sx={{
								alignItems: 'center',
								color: 'common.white',
								display: 'flex',
								gap: 1,
								textDecoration: 'none',
							}}>
							<RouterLink to="/" style={{ display: 'flex', textDecoration: 'none' }}>
								<Box sx={{ width: 150 }}>
									<LogoFundsChainAlt />
								</Box>
							</RouterLink>
						</Box>
					</Grid>

					<Grid item xs={8} md={9} lg={7}>
						<Grid
							alignItems="center"
							container
							columnSpacing={{ xs: 1, md: 2 }}
							justifyContent="flex-end">
							<Grid hidden item xs="auto" md={true}>
								<TextField
									color="white"
									fullWidth={true}
									sx={{ display: { xs: 'none !important', md: 'inline-flex !important' } }}
									InputProps={{
										disableUnderline: true,
										hiddenLabel: true,
										startAdornment: (
											<InputAdornment position="start">
												<IconSearch />
											</InputAdornment>
										),
									}}
									placeholder={t('search')}
									size="small"
									variant="filled"
								/>
							</Grid>

							<Grid item xs="auto" sx={{ display: { xs: 'none', md: 'inline-flex' } }}>
								<Grid alignItems="center" container spacing={2}>
									<Grid item xs="auto">
										<Grid alignItems="center" container spacing={1}>
											<Grid item>
												<Chip color="primary" label={t('new')} size="small" variant="filled" />
											</Grid>
											<Grid item>
												<Link
													color="common.white"
													component={RouterLink}
													to={`/features/r3110`}
													underline="hover">
													{t('release')} 3.11.0
												</Link>
											</Grid>
										</Grid>
									</Grid>
									<Grid
										item
										xs="auto"
										sx={{ display: { xs: 'none', md: 'none', lg: 'inline-flex' } }}>
										<LinkLearning />
									</Grid>
								</Grid>
							</Grid>

							{envName && (
								<Grid item xs="auto">
									<Chip color="error" label={envName} size="small" />
								</Grid>
							)}

							<Grid item xs="auto">
								<Grid container spacing={1}>
									<Grid item xs="auto">
										<IconButton color="white" onClick={handleOpenNotifications}>
											<IconNotifications />
										</IconButton>

										<Popover
											anchorEl={anchorElNotifications}
											anchorOrigin={{
												vertical: 'bottom',
												horizontal: 'center',
											}}
											onClose={handleCloseNotifications}
											open={Boolean(anchorElNotifications)}>
											<Box sx={{ maxHeight: 300, maxWidth: 300 }}>
												<Notifications items={activityItems} />
											</Box>
										</Popover>
									</Grid>

									{user && featureFlags['CRM'] && (
										<Grid item xs="auto">
											<>
												<Tooltip title={t('getHelp')}>
													<IconButton
														color="white"
														data-tour="getHelp"
														onClick={handleOpenFeedback}>
														<IconHelpOutline fontSize="inherit" />
													</IconButton>
												</Tooltip>

												<DialogAddIncident
													isOpen={paramDialog === 'addIncident'}
													onClose={handleCloseFeedback}
												/>
											</>
										</Grid>
									)}
								</Grid>
							</Grid>

							<Grid item xs="auto">
								<Grid alignItems="center" container columnSpacing={1}>
									<Grid item xs="auto">
										<Box>
											<Grid alignItems="center" container spacing={1}>
												<Grid item>
													<Box
														component="button"
														onClick={handleOpenAccount}
														sx={{ display: 'flex' }}>
														<Avatar src={user?.photo} />
													</Box>
												</Grid>
												<Grid
													item
													sx={{ display: { xs: 'none !important', md: 'inline-flex !important' } }}>
													{user?.firstName && (
														<Button
															color="white"
															disableRipple={true}
															onClick={handleOpenAccount}
															sx={{ '&:hover': { backgroundColor: 'transparent' } }}
															variant="text">
															{user?.firstName}
														</Button>
													)}
												</Grid>
											</Grid>
										</Box>
										<Popover
											anchorEl={anchorElAccount}
											anchorOrigin={{
												vertical: 'bottom',
												horizontal: 'left',
											}}
											onClose={handleCloseAccount}
											open={Boolean(anchorElAccount)}>
											<Box sx={{ width: 260 }}>
												<Box sx={{ padding: 3 }}>
													<Grid
														alignItems="center"
														container
														flexDirection="column"
														justifyItems="center"
														spacing={1}>
														<Grid item>
															<Avatar className="large" src={user?.photo} />
														</Grid>
														<Grid
															item
															sx={{
																maxWidth: '100%',
																textAlign: 'center',
																wordBreak: 'break-word',
															}}>
															<Typography variant="subtitle2">{user?.fullName}</Typography>
															{!userEntity && (
																<Typography
																	color="brand.grey.500"
																	component="div"
																	variant="caption">
																	World Bank Group
																</Typography>
															)}
														</Grid>
														{userEntity && (
															<Grid item sx={{ maxWidth: '100%' }}>
																<SelectUserEntity />
															</Grid>
														)}
														{userChipLabel && (
															<Grid item sx={{ maxWidth: '100%' }}>
																<Chip
																	label={userChipLabel}
																	size="small"
																	sx={{ display: 'inline-flex' }}
																	variant="twoTone"
																/>
															</Grid>
														)}
													</Grid>
												</Box>
												<MenuList disablePadding={true}>
													<MenuItem
														component="button"
														onClick={handleLogout}
														sx={{
															width: '100%',
															textAlign: 'left',
														}}>
														<ListItemText>{t('logout')}</ListItemText>
													</MenuItem>
												</MenuList>
											</Box>
										</Popover>
									</Grid>

									<Grid item xs="auto" sx={{ display: { lg: 'none' } }}>
										<IconButton color="white" onClick={onOpenNav}>
											<IconMenu />
										</IconButton>
									</Grid>
								</Grid>
							</Grid>
						</Grid>
					</Grid>
				</Grid>
			</Toolbar>
		</AppBar>
	);
};
